<template>
  <nav class="role-bottom-nav" aria-label="Role navigation">
    <button
      v-if="openGroup"
      type="button"
      class="role-bottom-nav__backdrop"
      aria-label="Close menu"
      @click="openGroup = null"
    />

    <transition name="bottom-sheet">
      <section v-if="openGroup" class="role-bottom-nav__sheet">
        <div class="role-bottom-nav__sheet-handle" />
        <h3>{{ $t(openGroup.label) }}</h3>
        <div class="role-bottom-nav__sheet-grid">
          <button
            v-for="child in openGroup.children"
            :key="`${child.path}-${child.tab || child.action || child.label}`"
            type="button"
            class="role-bottom-nav__sheet-item"
            :class="{ active: isActive(child) }"
            @click="navigate(child)"
          >
            <CIcon :name="child.icon" size="xl" />
            <span>{{ $t(child.label) }}</span>
          </button>
        </div>
      </section>
    </transition>

    <div class="role-bottom-nav__track">
      <button
        v-for="item in items"
        :key="item.path || item.label"
        type="button"
        class="role-bottom-nav__item"
        :class="{ active: isActive(item) }"
        @click="handleItem(item)"
      >
        <CIcon :name="item.icon" size="lg" class="role-bottom-nav__icon" />
        <span>{{ $t(item.label) }}</span>
      </button>
    </div>
  </nav>
</template>

<script>
const roleItems = {
  student_affairs: [
    { label: 'nav.dashboard', path: '/student-affairs/dashboard', icon: 'cil-speedometer' },
    { label: 'nav.main', path: '/student-affairs/home', icon: 'cil-home' },
    { label: 'nav.userManual', path: '/student-affairs/manual', icon: 'cil-book' },
  ],
  user: [
    { label: 'nav.main', path: '/user/mainuser', icon: 'cil-home' },
    { label: 'nav.history', path: '/user/history', icon: 'cil-history' },
    { label: 'nav.userManual', path: '/user/manual', icon: 'cil-book' },
  ],
  admin: [
    { label: 'nav.dashboard', path: '/admin/dashboard', icon: 'cil-speedometer' },
    { label: 'nav.main', path: '/admin/mainadmin', icon: 'cil-home' },
    { label: 'reportList.tabHistory', path: '/admin/history', icon: 'cil-history' },
    {
      label: 'nav.manageData',
      icon: 'cil-settings',
      children: [
        { label: 'nav.staff', path: '/admin/UsersList', icon: 'cil-people' },
        { label: 'nav.jobTypes', path: '/admin/job-type', icon: 'cil-briefcase' },
        { label: 'nav.buildings', path: '/admin/building', icon: 'cil-building' },
        { label: 'nav.requesterOrgSync', path: '/admin/requester-organization-sync', icon: 'cil-sync' },
        { label: 'nav.evaluation', path: '/admin/evaluation', icon: 'cil-star' },
      ],
    },
    { label: 'nav.userManual', path: '/admin/manual', icon: 'cil-book' },
  ],
  supervisor: [
    { label: 'nav.dashboard', path: '/supervisor/dashboard', icon: 'cil-speedometer' },
    { label: 'nav.main', path: '/supervisor/mainsupervisor', icon: 'cil-home' },
    { label: 'reportList.tabHistory', path: '/supervisor/history', icon: 'cil-history' },
    {
      label: 'nav.manageData',
      icon: 'cil-settings',
      children: [
        { label: 'nav.staff', path: '/supervisor/UsersList', icon: 'cil-people' },
        { label: 'nav.jobTypes', path: '/supervisor/job-type', icon: 'cil-briefcase' },
        { label: 'nav.evaluation', path: '/supervisor/evaluation', icon: 'cil-star' },
      ],
    },
    { label: 'nav.userManual', path: '/supervisor/manual', icon: 'cil-book' },
  ],
  technician: [
    { label: 'nav.dashboard', path: '/technician/dashboard', icon: 'cil-speedometer' },
    { label: 'nav.main', path: '/technician/maintechnician', icon: 'cil-home' },
    { label: 'reportList.tabHistory', path: '/technician/history', icon: 'cil-history' },
    { label: 'nav.userManual', path: '/technician/manual', icon: 'cil-book' },
  ],
}

export default {
  name: 'RoleBottomNav',
  data() {
    return { openGroup: null }
  },
  props: {
    role: {
      type: String,
      required: true,
      validator: value => Object.prototype.hasOwnProperty.call(roleItems, value),
    },
  },
  computed: {
    items() {
      return roleItems[this.role] || []
    },
  },
  mounted() {
    // Keep the navigation relative to the viewport. This also avoids iOS/iPadOS
    // treating `position: fixed` as relative to a transformed app wrapper.
    if (this.$el && this.$el.parentNode !== document.body) {
      document.body.appendChild(this.$el)
    }
  },
  beforeDestroy() {
    if (this.$el && this.$el.parentNode === document.body) {
      document.body.removeChild(this.$el)
    }
  },
  methods: {
    isActive(item) {
      if (item.children) return item.children.some(child => this.isActive(child))
      if (item.tab) {
        const currentTab = this.$route.query.tab || 'report'
        return this.$route.path === item.path && !this.$route.query.action && currentTab === item.tab
      }
      if (item.action) return this.$route.path === item.path && this.$route.query.action === item.action
      return this.$route.path === item.path || this.$route.path.startsWith(`${item.path}/`)
    },
    handleItem(item) {
      if (item.children) {
        this.openGroup = this.openGroup === item ? null : item
        return
      }
      this.navigate(item)
    },
    navigate(item) {
      this.openGroup = null
      const query = item.tab ? { tab: item.tab } : (item.action ? { action: item.action } : undefined)
      const target = query ? { path: item.path, query } : { path: item.path }
      const targetFullPath = item.tab
        ? `${item.path}?tab=${item.tab}`
        : (item.action ? `${item.path}?action=${item.action}` : item.path)
      if (this.$route.fullPath !== targetFullPath) this.$router.push(target)
    },
  },
}
</script>

<style scoped>
.role-bottom-nav {
  position: fixed !important;
  top: auto !important;
  right: auto !important;
  bottom: 0 !important;
  left: 0 !important;
  z-index: 1100;
  display: none;
  height: 72px;
  width: 100vw !important;
  max-width: 100vw !important;
  padding: 4px 10px;
  background: #ffffff !important;
  border-top: 1px solid rgba(37, 99, 235, 0.12) !important;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.03) !important;
  transform: none !important;
  visibility: visible !important;
  opacity: 1 !important;
  border-right: 0 !important;
  border-bottom: 0 !important;
  border-left: 0 !important;
  border-radius: 0 !important;
}

.role-bottom-nav__track {
  position: relative;
  z-index: 3;
  display: flex;
  align-items: stretch;
  width: 100%;
  height: 100%;
  overflow: hidden;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.role-bottom-nav__track::-webkit-scrollbar {
  display: none;
}

.role-bottom-nav__item {
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  max-width: none;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 4px 6px;
  color: #64748b;
  background: transparent;
  border: 0;
  outline: 0;
  font-size: 10px;
  font-weight: 500;
  line-height: 1.15;
  white-space: nowrap;
  border-radius: 0 !important;
  box-shadow: none !important;
  -webkit-tap-highlight-color: transparent;
}

.role-bottom-nav__item:focus,
.role-bottom-nav__item:focus-visible,
.role-bottom-nav__item:active {
  outline: 0 !important;
  border: 0 !important;
  box-shadow: none !important;
}

.role-bottom-nav__item.active {
  color: #1e40af;
  font-weight: 700;
}

.role-bottom-nav__icon {
  width: 24px;
  height: 24px;
}

.role-bottom-nav__backdrop {
  position: fixed;
  inset: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  padding: 0;
  background: rgba(15, 23, 42, 0.28);
  border: 0;
}

.role-bottom-nav__sheet {
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 2;
  width: calc(100% - 32px);
  max-width: 520px;
  padding: 14px 16px 20px;
  background: #ffffff !important;
  border: 1px solid rgba(37, 99, 235, 0.12) !important;
  border-radius: 16px;
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.15) !important;
  transform: translate(-50%, -50%);
}

.role-bottom-nav__sheet-handle {
  width: 42px;
  height: 4px;
  margin: 0 auto 12px;
  background: #cbd5e1;
  border-radius: 999px;
}

.role-bottom-nav__sheet h3 {
  margin: 0 0 14px;
  color: #1e40af;
  font-size: 1rem;
  font-weight: 800;
  text-align: center;
}

.role-bottom-nav__sheet-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  max-width: 620px;
  margin: 0 auto;
}

.role-bottom-nav__sheet-item {
  flex: 0 0 calc(33.333% - 7px);
  display: flex;
  min-height: 92px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 12px 8px;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid rgba(37, 99, 235, 0.10);
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.role-bottom-nav__sheet-item.active {
  color: #1e40af;
  background: #f0f9ff;
  border-color: rgba(37, 99, 235, 0.28);
  font-weight: 700;
}

.bottom-sheet-enter-active,
.bottom-sheet-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.bottom-sheet-enter,
.bottom-sheet-leave-to {
  opacity: 0;
  transform: translate(-50%, calc(-50% + 18px));
}

@media (max-width: 1200px) {
  .role-bottom-nav {
    display: block;
  }
}
</style>
